import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// prevent multiple hook instances from attaching duplicate handlers to same element
const boundElements = new WeakSet<HTMLElement>();

/**
 * useRouteScrollRestoration notes:
 * - This hook writes scrollTop for the containerSelector into sessionStorage under a key generated
 *   from `storagePrefix + pathname + search`. It always writes/reads using keyRef.current.
 * - It uses requestAnimationFrame for write throttling and avoids dataset indirection to eliminate
 *   stale-key races.
 * - boundElements (WeakSet) tracks which elements have been attached by ANY instance. Each hook
 *   instance uses boundRef to record whether *this* instance attached the listener; only then will
 *   it remove the listener and delete from boundElements in cleanup. This prevents accidental
 *   deletion of other instances' bindings.
 */

type Options = {
  containerSelector?: string;
  enabled?: boolean;
  storagePrefix?: string;
};

export function useRouteScrollRestoration({
  containerSelector = '.app-shell-body',
  enabled = true,
  storagePrefix = 'scroll@',
}: Options = {}) {
  const location = useLocation();

  // refs per requirements
  const rafRef = useRef<number | null>(null);
  const elRef = useRef<HTMLElement | null>(null);
  const keyRef = useRef<string>(`${storagePrefix}${location.pathname}${location.search}`);
  const prevLocationRef = useRef<typeof location | null>(null);

  // stable handler ref and bound flag to ensure single binding
  const handlerRef = useRef<EventListener | null>(null);
  const boundRef = useRef(false);

  // Effect A: bind listener only when enabled/containerSelector changes (not on every route change)
  useEffect(() => {
    if (!enabled) return;

    const el = document.querySelector(containerSelector) as HTMLElement | null;
    if (!el) return;

    elRef.current = el;

    // create a stable handler once
    if (!handlerRef.current) {
      handlerRef.current = (() => {
        return function onScroll() {
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
          }
          rafRef.current = requestAnimationFrame(() => {
            try {
              // always write using the current keyRef (no dataset indirection)
              sessionStorage.setItem(keyRef.current, String(elRef.current?.scrollTop ?? 0));
            } catch {
              // ignore
            } finally {
              rafRef.current = null;
            }
          });
        };
      })();
    }

    // attach if not already attached by ANY instance to this element
    if (handlerRef.current && !boundElements.has(el)) {
      el.addEventListener('scroll', handlerRef.current, { passive: true });
      boundElements.add(el);
      boundRef.current = true;
    }

    return () => {
      try {
        // final write using keyRef
        sessionStorage.setItem(keyRef.current, String(el.scrollTop));
      } catch {
        // ignore
      }
      if (boundRef.current && handlerRef.current) {
        try {
          el.removeEventListener('scroll', handlerRef.current);
        } catch (error) {
          // removeEventListener 失败通常是因为元素已被销毁，可以安全忽略
          // 但记录日志以便调试
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useRouteScrollRestoration] removeEventListener 失败:', error);
          }
        }
        boundRef.current = false;
        if (el && boundElements.has(el)) {
          boundElements.delete(el);
        }
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      elRef.current = null;
    };
    // only re-run when enabled or containerSelector or storagePrefix changes
  }, [enabled, containerSelector, storagePrefix]);

  // Effect B: only on location change update keyRef and restore scrollTop
  useEffect(() => {
    if (!enabled) return;

    // 检查是否是profile页面tab切换（只有tab参数变化）
    const shouldSkipScrollRestore = (() => {
      if (location.pathname !== '/profile') return false;

      const prevLocation = prevLocationRef.current;
      if (!prevLocation) return false;

      // 如果pathname相同，比较search参数
      if (prevLocation.pathname === location.pathname) {
        const prevParams = new URLSearchParams(prevLocation.search);
        const currentParams = new URLSearchParams(location.search);

        // 移除tab参数后比较其他参数是否相同
        const prevTab = prevParams.get('tab');
        const currentTab = currentParams.get('tab');

        prevParams.delete('tab');
        currentParams.delete('tab');

        // 如果除了tab参数外其他参数都相同，且tab确实发生了变化，则跳过scroll restoration
        return prevParams.toString() === currentParams.toString() && prevTab !== currentTab;
      }

      return false;
    })();

    // 更新prevLocation
    prevLocationRef.current = location;

    // 如果是profile页面tab切换，跳过scroll restoration
    if (shouldSkipScrollRestore) {
      // 仍然更新key，但不恢复滚动
      keyRef.current = `${storagePrefix}${location.pathname}${location.search}`;
      return;
    }

    keyRef.current = `${storagePrefix}${location.pathname}${location.search}`;

    const el = elRef.current ?? (document.querySelector(containerSelector) as HTMLElement | null);
    if (!el) return;

    try {
      const raw = sessionStorage.getItem(keyRef.current);
      const top = raw ? parseInt(raw, 10) || 0 : 0;
      requestAnimationFrame(() => {
        el.scrollTo({ top, behavior: 'auto' });
      });
    } catch {
      // ignore
    }
  }, [location.pathname, location.search, enabled, containerSelector, storagePrefix]);
}


